import {useContext, useState} from 'react'
import { useIntl } from 'react-intl'
import {Empty, Flex, Form, Modal, Spin, Tooltip} from 'antd'
import {ConnectorViewMappingContext} from './context/context'
import {putRequest} from '../../../services/base'
import {useParams} from 'react-router-dom'
import {NavContext} from '../../../context/global_context'
import {getRandomString} from '../../../utils/common'
import {notifyError, notifySuccess} from '../../../utils/notifications'
import {DeleteOutlined} from '@ant-design/icons'
import Button from '../../common/Button'
import InputText from '../../common/InputText'
import RecordsCount from '../../common/RecordsCount'

const DuplicateReferences = ({ disabled = false }) => {
  const intl = useIntl();
  const { connector_view } = useParams()
  const [state, dispatch] = useContext(ConnectorViewMappingContext)
  const [, navDispatch] = useContext(NavContext)

  const [showReferenceModal, setShowReferenceModal] = useState(false)
  const [referenceValue, setReferenceValue] = useState('')
  const [references, setReferences] = useState([])
  const [searchSelectedRefVal, setSearchSelectedRefVal] = useState('')
  const [selectedRefVal, setSelectedRefVal] = useState(null)
  const [modalLoading, setModalLoading] = useState(false)
  const [modal, contextHolder] = Modal.useModal()

  const selectReference = (ref) => {
    setSelectedRefVal(ref)
    getNewGroupIds(ref)
  }

  const getNewGroupIds = (reference) => {
    for (let index = 1; index < 1000; index++) {
      let tempRef = `${reference}_${index}`
      const referenceExists = references.some(
        (ref) => ref.new === tempRef
      )
      if (
        !state?.connector_view?.group_ids.includes(tempRef) &&
        !referenceExists
      ) {
        let temp = [...references]
        temp.push({ old: reference, new: tempRef })
        setReferences(temp)
        break
      }
    }
  }

  const saveDuplicateRef = async () => {
    modal.confirm({
      title: intl.formatMessage({id: 'connectorViews.mapping.confirmTitle'}),
      content: intl.formatMessage({id: 'connectorViews.mapping.saveReferencesConfirm'}),
      onOk: async () => {
        setModalLoading(true)
        try {
          let res = await putRequest(
            `/api/connector_views/save_references/${connector_view}/`,
            {
              references: references,
            }
          )
          if (res?.status) {
            notifySuccess(navDispatch, intl.formatMessage({id: 'connectorViews.mapping.duplicateReferenceSuccess'}))
            dispatch({
              type: 'connector_view',
              data: {
                ...state.connector_view,
                group_ids: res?.data?.group_ids,
                svg: `${res?.data.svg.split('?string=')[0]}?string=${getRandomString()}`,
              },
            })
            resetReferences()
          } else {
            notifyError(navDispatch, intl.formatMessage({id: 'connectorViews.mapping.duplicateReferenceFailed'}))
            resetReferences()
          }
        } catch (e) {
          notifyError(navDispatch, intl.formatMessage({id: 'connectorViews.mapping.duplicateReferenceFailed'}))
          resetReferences()
        } finally {
          setModalLoading(false)
        }
      },
    })
  }

  const resetReferences = () => {
    setShowReferenceModal(false)
    setReferenceValue('')
    setSearchSelectedRefVal('')
    setReferences([])
    setSelectedRefVal(null)
  }

  const deleteReference = (idx, reference) => {
    let tempReferences = [...references]
    tempReferences.splice(idx, 1)
    setReferences(tempReferences)
  }

  return (
    <div>
      {contextHolder}
      <Button
        type="primary"
        size="small"
        disabled={disabled}
        onClick={() => {
          setShowReferenceModal(true)
        }}
        testId="vv-duplicate-references"
      >
        {intl.formatMessage({id: 'connectorViews.mapping.duplicateReference'})}
      </Button>

      <Modal
        title={intl.formatMessage({id: 'connectorViews.mapping.selectReferencesTitle'})}
        open={showReferenceModal}
        onCancel={() => {
          setShowReferenceModal(false)
        }}
        width="80%"
        footer={null}
      >
        <Spin spinning={modalLoading}>
          <Form layout="vertical" size="small" key={showReferenceModal ? 'open' : 'closed'}>
            <div className="connector-mapping-duplicate-modal">
              <div className="connector-mapping-duplicate-left">
                <InputText
                  name="search_references"
                  type="search"
                  placeholder={intl.formatMessage({id: 'connectorViews.mapping.searchReferences'})}
                  isRequired={false}
                  hideLabel={true}
                  onChange={(val) => setReferenceValue(val)}
                />

                <div className="connector-mapping-duplicate-tags">
                  {state?.connector_view?.group_ids
                    ?.filter((gr) =>
                      gr
                        .toLocaleLowerCase()
                        .includes(referenceValue.toLocaleLowerCase())
                    )
                    .map((group, groupIdx) => {
                      return (
                        <div
                          className={`connector-mapping-duplicate-tag ${
                            selectedRefVal === group ? 'selected' : ''
                          }`}
                          key={`group-${groupIdx}`}
                          onClick={() => {
                            selectReference(group)
                          }}
                        >
                          {group}
                        </div>
                      )
                    })}
                </div>
              </div>
              <div className="connector-mapping-duplicate-right">
                <InputText
                  name="search_selected_refs"
                  type="search"
                  placeholder={intl.formatMessage({id: 'connectorViews.mapping.searchSelectedReferences'})}
                  isRequired={false}
                  hideLabel={true}
                  onChange={(val) => setSearchSelectedRefVal(val)}
                />
                
                <div className="connector-mapping-duplicate-tags">
                  {references
                    ?.filter((ref) =>
                      ref.new
                        .toLocaleLowerCase()
                        .includes(searchSelectedRefVal.toLocaleLowerCase())
                    )
                    .map((reference, referenceIdx) => {
                      return (
                        <div
                          className="connector-mapping-duplicate-selected-item"
                          key={`reference-${reference.new}`}
                        >
                          <Tooltip title={reference.new}>
                            <span className="duplicate-item-label">
                              {reference.new}
                            </span>
                          </Tooltip>
                          <span
                            className="duplicate-item-delete"
                            onClick={() =>
                              deleteReference(referenceIdx, reference)
                            }
                          >
                            <DeleteOutlined />
                          </span>
                        </div>
                      )
                    })}
                  {references?.length < 1 && (
                    <Flex vertical justify='center' align='center' style={{width: '100%', height: '100%'}}>
                      <Empty description={ <RecordsCount count={0} /> }  />
                    </Flex>
                  )}
                </div>
              </div>
            </div>
          </Form>
          <div className="connector-mapping-duplicate-footer">
            <Button
              type="primary"
              onClick={() => {
                saveDuplicateRef()
              }}
              disabled={references?.length < 1}
              testId="vv-duplicate-save"
            >
              {intl.formatMessage({id: 'connectorViews.mapping.saveDuplicateRefs'}, {count: references?.length})}
            </Button>
          </div>
        </Spin>
      </Modal>
    </div>
  )
}

export default DuplicateReferences
